import { ref, set, update, remove, push, onValue, onChildAdded, onDisconnect, serverTimestamp } from 'firebase/database';

// How Battle Arena talks to Firebase. Two versions with the same shape:
//  - firebaseNet: Firebase's live connection (the website)
//  - restNet: Firebase's web API, checked many times a second (inside Discord, whose proxy blocks the live connection)
// Paths are relative to the arena's own node (arena, or arenaActivity/<instance> in Discord).

export function firebaseNet(database, root = 'arena') {
  const at = (p) => ref(database, p ? `${root}/${p}` : root);
  return {
    onPlayers: (cb) => onValue(at('players'), (s) => cb(s.val() || {})),
    onRound: (cb) => onValue(at('round'), (s) => cb(s.val())),
    onClockOffset: (cb) => onValue(ref(database, '.info/serverTimeOffset'), (s) => cb(s.val() || 0)),
    onHits: (uid, cb) => onChildAdded(at(`players/${uid}/hits`), (s) => { remove(s.ref).catch(() => {}); cb(s.val()); }),
    set: (p, v) => set(at(p), v),
    update: (p, v) => update(at(p), v),
    remove: (p) => remove(at(p)),
    push: (p, v) => push(at(p), v),
    join: async (uid, v) => {
      await set(at(`players/${uid}`), { ...v, joinedAt: serverTimestamp() });
      onDisconnect(at(`players/${uid}`)).remove();
    },
    leave: (uid) => remove(at(`players/${uid}`)),
  };
}

const FAST_MS = 40;     // between reads during a battle (on top of however long each read takes)
const SLOW_MS = 800;    // between reads in the lobby
const BEAT_MS = 2000;   // how often you check in, so the host can tell you're still here
const TS = { '.sv': 'timestamp' };

export function restNet(base, root) {
  const url = (p) => `${base}/${p ? `${root}/${p}` : root}.json`;
  const send = (p, method, body, keepalive = false) =>
    fetch(url(p), { method, keepalive, body: body === undefined ? undefined : JSON.stringify(body) })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Firebase ${method} ${p}: HTTP ${r.status}`))));

  const playerCbs = new Set(), roundCbs = new Set(), offsetCbs = new Set(), hitCbs = new Map();
  const seenHits = new Set();
  let offset = 0, battle = false, timer = null, busy = false, again = false;
  const active = () => playerCbs.size || roundCbs.size || hitCbs.size;
  const setOffset = (serverTime, t0) => {
    if (typeof serverTime !== 'number') return;
    offset = serverTime - (t0 + Date.now()) / 2;
    offsetCbs.forEach((cb) => cb(offset));
  };

  // One read of the whole arena (the round and everyone in it), handed out to whoever's listening
  const poll = async () => {
    if (busy) { again = true; return; }
    clearTimeout(timer); timer = null;
    busy = true;
    try {
      const data = (await send('', 'GET')) || {};
      const players = data.players || {};
      battle = !!data.round?.phase && data.round.phase !== 'lobby';
      roundCbs.forEach((cb) => cb(data.round ?? null));
      playerCbs.forEach((cb) => cb(players));
      hitCbs.forEach((cb, uid) => {
        for (const [key, hit] of Object.entries(players[uid]?.hits || {})) {
          if (seenHits.has(key)) continue;
          seenHits.add(key);
          send(`players/${uid}/hits/${key}`, 'DELETE').catch(() => {});
          cb(hit);
        }
      });
    } catch { /* try again next time */ }
    busy = false;
    if (active()) timer = setTimeout(poll, again ? 0 : battle ? FAST_MS : SLOW_MS);
    again = false;
  };
  const listen = (set_, key, cb) => {
    if (key === undefined) set_.add(cb); else set_.set(key, cb);
    poll();
    return () => {
      if (key === undefined) set_.delete(cb); else set_.delete(key);
      if (!active()) { clearTimeout(timer); timer = null; }
    };
  };
  const refreshSoon = () => { if (!battle && active()) poll(); };  // in the lobby, show your own changes straight away

  // Updates to the same place go one at a time: while one is on its way, later ones are merged and sent next, so a
  // slow connection doesn't pile them up and the latest always arrives
  const inflight = new Set(), pending = new Map();
  const patch = (p, v) => {
    if (inflight.has(p)) { pending.set(p, { ...(pending.get(p) || {}), ...v }); return Promise.resolve(); }
    inflight.add(p);
    return send(p, 'PATCH', v).finally(() => {
      inflight.delete(p);
      const next = pending.get(p);
      pending.delete(p);
      if (next) patch(p, next).catch(() => {}); else refreshSoon();
    });
  };
  const forget = (p) => { for (const k of [...pending.keys()]) if (k === p || k.startsWith(`${p}/`)) pending.delete(k); };

  // You're in the arena: check in every few seconds (which also keeps the clocks lined up), and leave when the page closes
  let beat = null, onHide = null;
  const stopBeat = () => {
    clearInterval(beat); beat = null;
    if (onHide) window.removeEventListener('pagehide', onHide);
    onHide = null;
  };

  return {
    onPlayers: (cb) => listen(playerCbs, undefined, cb),
    onRound: (cb) => listen(roundCbs, undefined, cb),
    onClockOffset: (cb) => { offsetCbs.add(cb); cb(offset); return () => offsetCbs.delete(cb); },
    onHits: (uid, cb) => listen(hitCbs, uid, cb),
    set: (p, v) => { forget(p); return send(p, 'PUT', v).then(refreshSoon); },
    update: (p, v) => patch(p, v),
    remove: (p) => { forget(p); return send(p, 'DELETE').then(refreshSoon); },
    push: (p, v) => send(p, 'POST', v),
    join: async (uid, v) => {
      stopBeat();
      const t0 = Date.now();
      const saved = await send(`players/${uid}`, 'PUT', { ...v, joinedAt: TS, seen: TS });
      setOffset(saved?.seen, t0);
      beat = setInterval(() => {
        const t = Date.now();
        send(`players/${uid}`, 'PATCH', { seen: TS }).then((r) => setOffset(r?.seen, t)).catch(() => {});
      }, BEAT_MS);
      onHide = () => send(`players/${uid}`, 'DELETE', undefined, true).catch(() => {});
      window.addEventListener('pagehide', onHide);
      refreshSoon();
    },
    leave: (uid) => {
      stopBeat();
      forget(`players/${uid}`);
      return send(`players/${uid}`, 'DELETE').then(refreshSoon);
    },
  };
}
